import React from 'react';
import { ArrowRight } from 'lucide-react';
import { targetLabel, imageFileName } from './inlineEdit';

// One change, read-only: what it is, where, and before → after. Used by the
// review, success and under-review stages of the Visual Editor panel.

export function absoluteSrc(src, siteOrigin) {
  if (!src) return '';
  if (/^(https?:|data:|blob:)/i.test(src)) return src;
  try { return new URL(src, siteOrigin || window.location.origin).toString(); } catch { return src; }
}

// An uploaded picture, served back by the portal (same rules as attachments).
export function attachmentUrl(key) {
  if (!key || typeof key !== 'string') return '';
  return `/api/attachments/${key.split('/').map(encodeURIComponent).join('/')}`;
}

export function changeLabel(change) {
  return targetLabel(change?.kind === 'image' ? 'img' : change?.fingerprint?.tag);
}

// "New description: …" — or the decorative note.
export function NewDescription({ change, alt, decorative, className = '' }) {
  const isDecorative = decorative ?? change?.decorative;
  const text = alt ?? change?.after?.alt ?? '';
  if (isDecorative) return <p className={`text-[11px] text-gray-400 ${className}`}>Decorative picture — no description needed.</p>;
  return (
    <p className={`text-[11px] text-gray-300 break-words ${className}`}>
      <span className="text-gray-500">New description:</span> {text ? `“${text}”` : <em className="text-red-300 not-italic">none yet</em>}
    </p>
  );
}

export function PictureSwap({ beforeSrc, beforeLabel, afterSrc, afterLabel, size = 'w-16 h-12' }) {
  return (
    <div className="flex items-center gap-2">
      <img src={beforeSrc} alt={beforeLabel} className={`${size} object-cover rounded border border-white/[0.08] bg-white/[0.04]`} />
      <ArrowRight className="w-3.5 h-3.5 text-gray-600 shrink-0" aria-hidden="true" />
      {afterSrc ? (
        <img src={afterSrc} alt={afterLabel} className={`${size} object-cover rounded border border-[#B0FC38]/30 bg-white/[0.04]`} />
      ) : (
        <span className="text-[11px] text-gray-500">New picture</span>
      )}
    </div>
  );
}

export default function ChangeSummary({ change, siteOrigin, previewSrc, headingId }) {
  const isImage = change?.kind === 'image';
  const label = changeLabel(change);
  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      <div className="flex items-baseline gap-2 min-w-0">
        <span id={headingId} className="text-[13px] font-medium text-white">{label}</span>
        <span className="text-[11px] text-gray-500 font-mono truncate">{change?.path}</span>
      </div>
      {isImage ? (
        <>
          <PictureSwap
            beforeSrc={absoluteSrc(change.before?.src, siteOrigin)}
            beforeLabel={`Current picture: ${imageFileName(change.before?.src)}`}
            afterSrc={previewSrc || attachmentUrl(change.after?.attachmentKey)}
            afterLabel={`New picture: ${change.after?.filename || ''}`}
          />
          <NewDescription change={change} />
        </>
      ) : (
        <div className="text-[12px] leading-relaxed">
          <p className="text-gray-500 line-through break-words"><span className="sr-only">Before: </span>{change?.before?.text}</p>
          <p className="text-white break-words"><span className="sr-only">After: </span>{change?.after?.text || <em className="text-gray-500">(empty)</em>}</p>
        </div>
      )}
    </div>
  );
}
